import classNames from "classnames";
import { memo, type FC } from "react";
import type { PlaneCoordinate, PlaneScreenPoint, ToScreenPoint } from "./CartesianPlane";
import styles from "./DeckShapeLayer.module.css";

type DeckShapeLayerProps = {
  coordinates: PlaneCoordinate[];
  maxCoordinates: number;
  // The auto-computed final point that would close the shape if previewCoordinate were placed
  // (only set while positioning the point right before auto-close) - lets the full outline,
  // closing edges included, preview live instead of just the single next segment.
  closingPreviewCoordinate?: PlaneCoordinate | null;
  isPreviewInvalid?: boolean;
  outlineClassName: string;
  previewCoordinate?: PlaneCoordinate | null;
  shapeClassName: string;
  toScreenPoint: ToScreenPoint;
};

const getScreenPoints = (coordinates: PlaneCoordinate[], toScreenPoint: ToScreenPoint): PlaneScreenPoint[] =>
  coordinates
    .map((coordinate, index) => toScreenPoint(coordinate, index))
    .filter((point): point is PlaneScreenPoint => point !== null);

const getPointsAttribute = (points: PlaneScreenPoint[]): string =>
  points.map((point) => `${point.pixelX},${point.pixelY}`).join(" ");

const DeckShapeLayer: FC<DeckShapeLayerProps> = ({
  coordinates,
  maxCoordinates,
  closingPreviewCoordinate,
  isPreviewInvalid,
  outlineClassName,
  previewCoordinate,
  shapeClassName,
  toScreenPoint,
}) => {
  const confirmedPoints = getScreenPoints(coordinates, toScreenPoint);
  const confirmedPointsAttribute = getPointsAttribute(confirmedPoints);
  const previewPoint =
    previewCoordinate && coordinates.length > 0 ? toScreenPoint(previewCoordinate, coordinates.length) : null;
  const closingPreviewPoint =
    closingPreviewCoordinate && coordinates.length > 0
      ? toScreenPoint(closingPreviewCoordinate, coordinates.length + 1)
      : null;
  const firstConfirmedPoint = confirmedPoints[0];
  const lastConfirmedPoint = confirmedPoints[confirmedPoints.length - 1];
  const hasConfirmedPath = confirmedPoints.length > 1;
  const hasPreviewPath = !!lastConfirmedPoint && !!previewPoint;
  const previewLineClassName = classNames(styles.previewOutline, {
    [styles.previewOutlineInvalid]: isPreviewInvalid,
  });

  if (!hasConfirmedPath && !hasPreviewPath) {
    return null;
  }

  return (
    <svg className={styles.shapeLayer} aria-hidden="true">
      {coordinates.length === maxCoordinates ? (
        <polygon className={shapeClassName} points={confirmedPointsAttribute} />
      ) : (
        hasConfirmedPath && <polyline className={outlineClassName} points={confirmedPointsAttribute} />
      )}
      {lastConfirmedPoint && previewPoint && (
        <line
          className={previewLineClassName}
          x1={lastConfirmedPoint.pixelX}
          y1={lastConfirmedPoint.pixelY}
          x2={previewPoint.pixelX}
          y2={previewPoint.pixelY}
        />
      )}
      {previewPoint && closingPreviewPoint && (
        <line
          className={previewLineClassName}
          x1={previewPoint.pixelX}
          y1={previewPoint.pixelY}
          x2={closingPreviewPoint.pixelX}
          y2={closingPreviewPoint.pixelY}
        />
      )}
      {closingPreviewPoint && firstConfirmedPoint && (
        <line
          className={previewLineClassName}
          x1={closingPreviewPoint.pixelX}
          y1={closingPreviewPoint.pixelY}
          x2={firstConfirmedPoint.pixelX}
          y2={firstConfirmedPoint.pixelY}
        />
      )}
    </svg>
  );
};

// Memoized: the plane's parents re-render while the pointer moves (e.g. the drawing preview), this
// layer only when its own shape, preview or view does.
export default memo(DeckShapeLayer);
